import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { doc, updateDoc, arrayUnion, arrayRemove, collection, getDocs, query, where } from 'firebase/firestore';
import { toast } from 'react-toastify';
import {
  LuCloudUpload,
  LuDownload,
  LuEye,
  LuFile,
  LuFileImage,
  LuFileSpreadsheet,
  LuFileText,
  LuFolderOpen,
  LuInbox,
  LuInfo,
  LuLoaderCircle,
  LuTrash2,
  LuTriangleAlert,
  LuUpload,
} from 'react-icons/lu';
import { db, storage } from '../../config/firebaseConfig';
import { downloadFileAs } from '../../utils/downloadFile';
import { CV_DOCUMENT_TYPES } from '../../utils/cvDocuments';
import {
  EXIT_CONTRACT_CATEGORY,
  isActiveEmployee,
  todayDateInput,
} from '../../utils/employeeExit';
import Button from '../../components/ui/Button';
import GlassIcon from '../../components/ui/GlassIcon';
import IconButton from '../../components/ui/IconButton';
import Modal from '../../components/ui/Modal';
import SegmentedControl from '../../components/ui/SegmentedControl';
import { GLASS_CHIP, ICON_BUTTON } from '../../components/ui/glass';
import { useTranslation } from '../../i18n/LanguageContext';
// Only the global variables of this stylesheet are still in use (by other
// legacy dialogs); importing it keeps its place in the cascade.
import './FileUploadModal.module.css';
import ExitDateModal from './ExitDateModal';
import { Field, SelectInput } from './employeeForm/FormControls';
import { FILE_CATEGORIES, cvTypeLabel, fileCategoryLabel } from './employeeForm/employeeFormOptions';

// Icon and tint per file type (MIME type or extension)
const fileVisual = (fileType = '') => {
  const type = fileType.toLowerCase();
  if (type.includes('pdf')) return { icon: LuFileText, tint: 'tw-bg-rose-500/10 tw-text-rose-600 dark:tw-text-rose-300' };
  if (type.includes('excel') || type.includes('sheet') || type.includes('xls')) return { icon: LuFileSpreadsheet, tint: 'tw-bg-emerald-500/10 tw-text-emerald-600 dark:tw-text-emerald-300' };
  if (type.includes('image') || type.includes('png') || type.includes('jpg') || type.includes('jpeg')) return { icon: LuFileImage, tint: 'tw-bg-violet-500/10 tw-text-violet-600 dark:tw-text-violet-300' };
  if (type.includes('word') || type.includes('doc')) return { icon: LuFileText, tint: 'tw-bg-blue-500/10 tw-text-blue-600 dark:tw-text-blue-300' };
  return { icon: LuFile, tint: 'tw-bg-slate-500/10 tw-text-slate-500 dark:tw-text-slate-300' };
};

const createSafeStoragePath = (documentId, fileName) => {
  // Ensure we have valid inputs
  if (!documentId || !fileName) {
    throw new Error('Invalid document ID or file name');
  }

  // Convert filename to string and get extension
  const fileNameString = String(fileName);
  const fileExtension = fileNameString.split('.').pop().toLowerCase();
  const timestamp = new Date().getTime();

  // Create a safe path using just the document ID and timestamp
  return `employees/${documentId}/${timestamp}.${fileExtension}`;
};

const EmptyList = ({ children }) => (
  <div className="tw-flex tw-flex-col tw-items-center tw-gap-3 tw-rounded-2xl tw-border tw-border-dashed tw-border-slate-300 tw-px-6 tw-py-10 tw-text-center dark:tw-border-white/15">
    <LuInbox aria-hidden="true" className="tw-h-7 tw-w-7 tw-text-slate-400 dark:tw-text-slate-500" />
    <p className="tw-text-sm tw-font-medium tw-text-slate-500 dark:tw-text-slate-400">{children}</p>
  </div>
);

EmptyList.propTypes = { children: PropTypes.node };

/**
 * "Dosyalar": employee documents (upload by type, list, download, delete)
 * and the employee's own CV/GCP/IATA uploads. Parent updates go through
 * `onClose(updatedEmployee, keepOpen)`; a plain close calls `onClose()`.
 */
const FileUploadModal = ({ employee, onClose }) => {
  const { t, language, locale } = useTranslation();
  const idPrefix = useId();
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [fileCategory, setFileCategory] = useState('');
  const [categoryError, setCategoryError] = useState(false);
  const [cvDocuments, setCVDocuments] = useState([]);
  const [cvLoading, setCVLoading] = useState(false);
  const [tab, setTab] = useState('files');
  const [confirmingPath, setConfirmingPath] = useState(null);
  // "İşten Ayrılış Sözleşme" yüklendiğinde açılan çıkış tarihi penceresi
  const [showExitDatePrompt, setShowExitDatePrompt] = useState(false);
  const inputRef = useRef(null);

  const files = employee?.files || [];
  const uploadedCategories = new Set(files.map((file) => file.category));
  const fullName = `${employee?.eName || ''} ${employee?.eSurname || ''}`.trim();
  const categoryId = `${idPrefix}-category`;

  const handleClose = () => {
    if (!uploading) onClose();
  };

  // Fetch CV/GCP/IATA documents
  const fetchCVDocuments = useCallback(async () => {
    if (!employee?.id) return;

    setCVLoading(true);
    try {
      const q = query(
        collection(db, 'CVDocuments'),
        where('employeeId', '==', employee.id)
      );

      const snapshot = await getDocs(q);
      const docs = snapshot.docs.map(d => ({
        id: d.id,
        ...d.data()
      }));

      // Manuel sıralama - client-side
      docs.sort((a, b) => {
        const dateA = a.createdAt?.toDate?.() || new Date(a.uploadedAt || 0);
        const dateB = b.createdAt?.toDate?.() || new Date(b.uploadedAt || 0);
        return dateB - dateA; // Yeni dosyalar önce
      });

      setCVDocuments(docs);
    } catch (error) {
      console.error('CV belgeleri yüklenemedi:', error);
    } finally {
      setCVLoading(false);
    }
  }, [employee?.id]);

  // Load CV documents when modal opens
  useEffect(() => {
    fetchCVDocuments();
  }, [fetchCVDocuments]);

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const handleFiles = async (selectedFiles) => {
    if (!fileCategory) {
      toast.error(t('employees.modals.files.toast.chooseCategory'));
      setCategoryError(true);
      return;
    }

    // Check if a file with the same category already exists
    const existingFile = employee?.files?.find(file => file.category === fileCategory);
    if (existingFile) {
      toast.error(t('employees.modals.files.toast.duplicate', { category: fileCategoryLabel(t, fileCategory) }));
      return;
    }

    setUploading(true);
    try {
      const documentId = employee?.id;

      if (!documentId) {
        throw new Error('Invalid employee document ID');
      }

      // Convert FileList to Array
      const filesArray = Array.from(selectedFiles);
      const uploadedFiles = [];

      for (const file of filesArray) {
        const storagePath = createSafeStoragePath(documentId, file.name);
        const storageRef = ref(storage, storagePath);
        // Storage'daki nesne adı zaman damgasından üretiliyor; dosyanın özgün
        // adı contentDisposition ile saklanır ki adres doğrudan açıldığında da
        // doğru adla inisin. (UTF-8 filename* Türkçe karakterler için gerekli.)
        // "attachment" değil "inline": böylece PDF/resim önizlemesi tarayıcıda
        // açılmaya devam eder, indirilen dosya yine özgün adı alır.
        await uploadBytes(storageRef, file, {
          contentType: file.type || undefined,
          contentDisposition: `inline; filename*=UTF-8''${encodeURIComponent(file.name)}`,
        });
        const downloadURL = await getDownloadURL(storageRef);
        const employeeRef = doc(db, 'Employees', documentId);

        const newFile = {
          name: file.name,
          path: storagePath,
          type: file.type || file.name.split('.').pop().toLowerCase(),
          url: downloadURL,
          uploadDate: new Date().toISOString(),
          category: fileCategory
        };

        await updateDoc(employeeRef, {
          files: arrayUnion(newFile)
        });

        uploadedFiles.push(newFile);
      }

      toast.success(t('employees.modals.files.toast.uploaded'));

      // Update the local employee object with the new files
      const updatedEmployee = {
        ...employee,
        files: [...(employee.files || []), ...uploadedFiles]
      };

      // Update the parent component with new data but keep modal open
      onClose(updatedEmployee, true);
      setTab('files');

      // Clear the file input
      if (inputRef.current) {
        inputRef.current.value = '';
      }

      // İşten ayrılış sözleşmesi yüklendiyse çıkış sürecini başlat: tarih elle
      // girilir, ardından çalışan pasife alınır (giriş yetkisi kalkar) ve
      // projelerdeki atamaları pasife düşer.
      if (fileCategory === EXIT_CONTRACT_CATEGORY && isActiveEmployee(employee)) {
        setShowExitDatePrompt(true);
      }
    } catch (error) {
      console.error('Error uploading files:', error);
      toast.error(t('employees.modals.files.toast.uploadError'));
    }
    setUploading(false);
  };

  const handleDeleteFile = async (file) => {
    setDeleting(true);
    try {
      // Delete from Firebase Storage
      const storageRef = ref(storage, file.path);
      await deleteObject(storageRef);

      // Remove from Firestore
      const employeeRef = doc(db, 'Employees', employee.id);
      await updateDoc(employeeRef, {
        files: arrayRemove(file)
      });

      // Update local state
      const updatedEmployee = {
        ...employee,
        files: employee.files.filter(f => f.path !== file.path)
      };

      // Update parent component
      onClose(updatedEmployee, true);
      toast.success(t('employees.modals.files.toast.deleted'));
    } catch (error) {
      console.error('Error deleting file:', error);
      toast.error(t('employees.modals.files.toast.deleteError'));
    }
    setConfirmingPath(null);
    setDeleting(false);
  };

  // The picker only opens once a file type is chosen
  const openPicker = () => {
    if (uploading) return;
    if (!fileCategory) {
      setCategoryError(true);
      document.getElementById(categoryId)?.focus();
      return;
    }
    inputRef.current?.click();
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (uploading) return;
    await handleFiles(e.dataTransfer.files);
  };

  const categoryHint = fileCategory && uploadedCategories.has(fileCategory) ? (
    <span className="tw-inline-flex tw-items-start tw-gap-1 tw-text-amber-700 dark:tw-text-amber-300">
      <LuTriangleAlert aria-hidden="true" className="tw-mt-px tw-h-3.5 tw-w-3.5 tw-shrink-0" />
      {t('employees.modals.files.alreadyUploaded')}
    </span>
  ) : undefined;

  const renderFiles = () => {
    if (files.length === 0) return <EmptyList>{t('employees.modals.files.none')}</EmptyList>;
    return (
      <ul className="tw-space-y-2">
        {files.map((file, index) => {
          const { icon: Icon, tint } = fileVisual(file.type || '');
          const confirming = confirmingPath === file.path;
          return (
            <li key={file.path || index} className={`tw-flex tw-items-center tw-gap-3 tw-rounded-2xl tw-p-2.5 ${GLASS_CHIP}`}>
              <span className={`tw-grid tw-h-10 tw-w-10 tw-shrink-0 tw-place-items-center tw-rounded-xl ${tint}`}>
                <Icon aria-hidden="true" className="tw-h-5 tw-w-5" />
              </span>
              <div className="tw-min-w-0 tw-flex-1">
                <a
                  href={file.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={t('employees.modals.files.open')}
                  className="tw-block tw-truncate tw-text-sm tw-font-semibold tw-text-slate-800 tw-transition-colors hover:tw-text-teal-700 dark:tw-text-slate-100 dark:hover:tw-text-teal-300"
                >
                  {file.name}
                </a>
                <p className="tw-mt-1 tw-flex tw-min-w-0 tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1 tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">
                  <span className="tw-max-w-full tw-truncate tw-rounded-full tw-bg-violet-500/10 tw-px-2 tw-py-0.5 tw-font-semibold tw-text-violet-700 dark:tw-text-violet-300">
                    {file.category ? fileCategoryLabel(t, file.category) : t('employees.modals.files.uncategorized')}
                  </span>
                  {file.uploadDate && <span className="tw-tabular-nums">{formatDate(file.uploadDate)}</span>}
                </p>
              </div>
              {confirming ? (
                <div className="tw-flex tw-shrink-0 tw-items-center tw-gap-1.5">
                  <span className="tw-hidden tw-text-xs tw-font-semibold tw-text-rose-600 sm:tw-inline dark:tw-text-rose-400">
                    {t('employees.modals.files.confirmDelete')}
                  </span>
                  <Button size="sm" variant="ghost" onClick={() => setConfirmingPath(null)} disabled={deleting}>
                    {t('employees.modals.files.cancel')}
                  </Button>
                  <Button size="sm" variant="danger" onClick={() => handleDeleteFile(file)} disabled={deleting}>
                    {deleting
                      ? <LuLoaderCircle aria-hidden="true" className="tw-h-3.5 tw-w-3.5 tw-animate-spin" />
                      : <LuTrash2 aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />}
                    {t('employees.modals.files.confirm')}
                  </Button>
                </div>
              ) : (
                <div className="tw-flex tw-shrink-0 tw-items-center tw-gap-1.5">
                  <IconButton label={t('employees.modals.files.download')} onClick={() => downloadFileAs(file.url, file.name)}>
                    <LuDownload aria-hidden="true" className="tw-h-4 tw-w-4" />
                  </IconButton>
                  <IconButton
                    label={t('employees.modals.files.delete')}
                    tone="alert"
                    onClick={() => setConfirmingPath(file.path)}
                    disabled={deleting}
                    className="disabled:tw-opacity-50"
                  >
                    <LuTrash2 aria-hidden="true" className="tw-h-4 tw-w-4" />
                  </IconButton>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    );
  };

  const renderCvDocuments = () => {
    if (cvLoading) {
      return (
        <div className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-py-10 tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">
          <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin" />
          {t('employees.modals.files.loading')}
        </div>
      );
    }
    if (cvDocuments.length === 0) return <EmptyList>{t('employees.modals.files.cvNone')}</EmptyList>;
    return (
      <div className="tw-space-y-4">
        {CV_DOCUMENT_TYPES.map((docType) => {
          const typeDocs = cvDocuments.filter((d) => d.documentType === docType.key);
          if (typeDocs.length === 0) return null;
          return (
            <section key={docType.key}>
              <h4 className="tw-mb-1.5 tw-px-1 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-[0.12em] tw-text-slate-400 dark:tw-text-slate-500">
                {cvTypeLabel(t, docType.key, docType.label)}
              </h4>
              <ul className="tw-space-y-2">
                {typeDocs.map((cvDoc) => {
                  const isPdf = cvDoc.fileType === 'pdf';
                  return (
                    <li key={cvDoc.id} className={`tw-flex tw-items-center tw-gap-3 tw-rounded-2xl tw-p-2.5 ${GLASS_CHIP}`}>
                      <span className={`tw-grid tw-h-10 tw-w-10 tw-shrink-0 tw-place-items-center tw-rounded-xl ${isPdf ? 'tw-bg-rose-500/10 tw-text-rose-600 dark:tw-text-rose-300' : 'tw-bg-blue-500/10 tw-text-blue-600 dark:tw-text-blue-300'}`}>
                        <LuFileText aria-hidden="true" className="tw-h-5 tw-w-5" />
                      </span>
                      <div className="tw-min-w-0 tw-flex-1">
                        <p title={cvDoc.fileName} className="tw-truncate tw-text-sm tw-font-semibold tw-text-slate-800 dark:tw-text-slate-100">{cvDoc.fileName}</p>
                        <p className="tw-mt-0.5 tw-flex tw-flex-wrap tw-gap-x-2 tw-text-xs tw-text-slate-500 tw-tabular-nums dark:tw-text-slate-400">
                          <span>{formatDate(cvDoc.uploadedAt)}</span>
                          <span>{(cvDoc.fileSize / 1024).toFixed(2)} KB</span>
                        </p>
                      </div>
                      <div className="tw-flex tw-shrink-0 tw-items-center tw-gap-1.5">
                        <a
                          href={cvDoc.fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={t('employees.modals.files.view')}
                          aria-label={t('employees.modals.files.view')}
                          className={`${ICON_BUTTON} tw-text-slate-600 hover:tw-text-slate-900 dark:tw-text-slate-300 dark:hover:tw-text-white`}
                        >
                          <LuEye aria-hidden="true" className="tw-h-4 tw-w-4" />
                        </a>
                        <IconButton label={t('employees.modals.files.download')} onClick={() => downloadFileAs(cvDoc.fileUrl, cvDoc.fileName)}>
                          <LuDownload aria-hidden="true" className="tw-h-4 tw-w-4" />
                        </IconButton>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>
          );
        })}
      </div>
    );
  };

  const tabLabel = (text, count) => (
    <span className="tw-inline-flex tw-items-center tw-gap-1.5">
      {text}
      <span className="tw-rounded-full tw-bg-slate-900/[0.07] tw-px-1.5 tw-text-[10px] tw-font-bold tw-tabular-nums dark:tw-bg-white/10">{count}</span>
    </span>
  );

  return (
    <>
      <Modal
        isOpen
        onClose={handleClose}
        size="form"
        title={t('employees.modals.files.title')}
        subtitle={fullName || undefined}
        icon={(
          <GlassIcon gradient="tw-from-violet-400 tw-to-purple-600 tw-shadow-violet-500/30" size="xs">
            <LuFolderOpen aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
          </GlassIcon>
        )}
        closeLabel={t('common.close')}
        lang={language}
        footer={<Button variant="ghost" onClick={handleClose} disabled={uploading}>{t('common.close')}</Button>}
      >
        <div className="tw-grid tw-gap-5 md:tw-grid-cols-[minmax(0,17rem)_minmax(0,1fr)]">
          {/* Upload */}
          <div className="tw-space-y-3">
            <Field
              label={t('employees.modals.files.category')}
              htmlFor={categoryId}
              required
              error={categoryError ? t('employees.modals.files.chooseCategory') : undefined}
              hint={categoryHint}
            >
              <SelectInput
                id={categoryId}
                value={fileCategory}
                onChange={(e) => {
                  setFileCategory(e.target.value);
                  setCategoryError(false);
                }}
                disabled={uploading}
                placeholder={t('employees.form.choose')}
                error={categoryError ? t('employees.modals.files.chooseCategory') : undefined}
                options={FILE_CATEGORIES.map((category) => ({
                  value: category.value,
                  label: uploadedCategories.has(category.value)
                    ? t('employees.modals.files.uploadedMark', { label: fileCategoryLabel(t, category.value) })
                    : fileCategoryLabel(t, category.value),
                }))}
              />
            </Field>

            <input
              ref={inputRef}
              type="file"
              multiple
              onChange={(e) => handleFiles(e.target.files)}
              accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
              className="tw-hidden"
              disabled={uploading}
            />

            <div
              role="button"
              tabIndex={0}
              aria-disabled={uploading || undefined}
              onClick={openPicker}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openPicker();
                }
              }}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              className={`tw-flex tw-cursor-pointer tw-flex-col tw-items-center tw-gap-2 tw-rounded-2xl tw-border-2 tw-border-dashed tw-px-4 tw-py-7 tw-text-center tw-transition-colors focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 ${
                dragActive
                  ? 'tw-border-teal-500 tw-bg-teal-500/10'
                  : 'tw-border-slate-300 tw-bg-slate-50/60 hover:tw-border-teal-400 hover:tw-bg-teal-500/[0.04] dark:tw-border-white/15 dark:tw-bg-white/[0.02] dark:hover:tw-border-teal-400/60'
              }`}
            >
              <GlassIcon gradient="tw-from-teal-400 tw-to-cyan-600 tw-shadow-teal-500/30" size="md">
                {uploading
                  ? <LuLoaderCircle aria-hidden="true" className="tw-h-6 tw-w-6 tw-animate-spin" />
                  : <LuCloudUpload aria-hidden="true" className="tw-h-6 tw-w-6" />}
              </GlassIcon>
              {uploading ? (
                <p className="tw-text-sm tw-font-semibold tw-text-slate-700 dark:tw-text-slate-200">{t('employees.modals.files.uploading')}</p>
              ) : (
                <>
                  <p className="tw-text-sm tw-font-semibold tw-text-slate-700 dark:tw-text-slate-200">{t('employees.modals.files.drop')}</p>
                  <p className="tw-text-xs tw-text-slate-400">{t('employees.modals.files.or')}</p>
                  <Button
                    size="sm"
                    variant="primary"
                    disabled={uploading}
                    onClick={(e) => {
                      e.stopPropagation();
                      openPicker();
                    }}
                  >
                    <LuUpload aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
                    {t('employees.modals.files.browse')}
                  </Button>
                  <p className="tw-text-[11px] tw-text-slate-400 dark:tw-text-slate-500">{t('employees.modals.files.accepted')}</p>
                </>
              )}
            </div>
          </div>

          {/* Lists */}
          <div className="tw-min-w-0 tw-space-y-3">
            <SegmentedControl
              options={[
                { value: 'files', label: tabLabel(t('employees.modals.files.uploadedTab'), files.length) },
                { value: 'cv', label: tabLabel(t('employees.modals.files.cvTab'), cvDocuments.length) },
              ]}
              value={tab}
              onChange={setTab}
              layoutId={`${idPrefix}-tab`}
              label={t('employees.modals.files.title')}
              size="sm"
            />
            {tab === 'cv' && (
              <p className="tw-flex tw-items-start tw-gap-2 tw-text-xs tw-leading-snug tw-text-slate-500 dark:tw-text-slate-400">
                <LuInfo aria-hidden="true" className="tw-mt-px tw-h-3.5 tw-w-3.5 tw-shrink-0" />
                {t('employees.modals.files.cvHint')}
              </p>
            )}
            <div className="tw-pr-1 md:tw-max-h-[min(24rem,52vh)] md:tw-overflow-y-auto glass-scrollbar">
              {tab === 'files' ? renderFiles() : renderCvDocuments()}
            </div>
          </div>
        </div>
      </Modal>

      {/* Çıkış sözleşmesi yüklendi: tarihi sor ve çıkışı tamamla */}
      {showExitDatePrompt && (
        <ExitDateModal
          selectedEmployee={employee}
          reason={t('employees.modals.files.exitReason', { name: fullName })}
          defaultDate={todayDateInput()}
          onClose={() => setShowExitDatePrompt(false)}
          onUpdate={async () => {}}
          onEmployeeUpdate={(updatedEmployee) => {
            // Üst bileşendeki liste ve seçili çalışan adası güncel kalsın
            onClose(updatedEmployee, true);
          }}
        />
      )}
    </>
  );
};

FileUploadModal.propTypes = {
  employee: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default FileUploadModal;
